import type { TicketAlertClockConfig } from '@troia-v3/types';

/**
 * Relógio de ESTAGNAÇÃO da etapa — tempo desde `stageEnteredAt` contra o
 * `alertConfig` resolvido da etapa. NÃO é SLA: não lê `sla.clocks`, não usa
 * horário comercial, não conhece política. Ver o cabeçalho de
 * DOCS/superpowers/plans/2026-07-28-sla-c-superficies-helpdesk.md.
 */

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/**
 * Idade na etapa no formato do mockup Helpdesk 07 (`helpdesk.html:942`):
 * "há 12 min" · "há 5h" · "há 2d 4h" · "há 9d".
 *
 * Diferente de `formatStageAge` (utils/index.ts:47), que é do Helpdesk 01/05 e
 * devolve "há 2 dias" — os dois formatos coexistem de propósito: o card 07
 * precisa da resolução de horas dentro do dia porque é ela que o trilho mede.
 */
export function formatStageClock(iso: string | undefined, now: number = Date.now()): string {
  if (!iso) return 'sem data';
  const started = new Date(iso).getTime();
  if (Number.isNaN(started)) return 'data inválida';
  const ms = Math.max(0, now - started);
  if (ms < 60_000) return 'agora';
  if (ms < HOUR_MS) return `há ${Math.floor(ms / 60_000)} min`;
  if (ms < DAY_MS) return `há ${Math.floor(ms / HOUR_MS)}h`;
  const days = Math.floor(ms / DAY_MS);
  const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
  return hours === 0 ? `há ${days}d` : `há ${days}d ${hours}h`;
}

export type StageClockLevel = 'off' | 'ok' | 'warning' | 'critical';

export interface StageClockView {
  /** Texto do canto direito da primeira linha — `.hd2-stage-time`. */
  label: string;
  level: StageClockLevel;
  /** Largura do preenchimento, 0..100 inteiro. `0` quando `level === 'off'`. */
  percent: number;
  /** Cor do texto do tempo — equivalente de `--c3-tone-fg`. */
  toneFg: string;
  /** Cor do preenchimento do trilho — equivalente de `--c3-tone-solid`. */
  toneSolid: string;
}

const TONE: Record<StageClockLevel, { fg: string; solid: string }> = {
  critical: { fg: 'var(--theme-error-fg)', solid: 'var(--theme-error-color)' },
  warning: { fg: 'var(--theme-warning-fg)', solid: 'var(--theme-warning-color)' },
  ok: { fg: 'var(--theme-muted-text)', solid: 'var(--theme-card-muted, var(--theme-muted-text))' },
  off: { fg: 'var(--theme-muted-text)', solid: 'transparent' },
};

export function computeStageClock(
  iso: string | undefined,
  clock: TicketAlertClockConfig | undefined,
  now: number = Date.now(),
): StageClockView {
  const label = formatStageClock(iso, now);
  const started = iso ? new Date(iso).getTime() : Number.NaN;

  // Relógio desligado, ausente, ou data ilegível: o card mostra o tempo em cinza
  // e NÃO desenha o trilho. Trilho sem limiar é decoração, e decoração colorida
  // é proibida pela fundação (`cards-v3.css:378-380`).
  if (!clock || !clock.enabled || Number.isNaN(started)) {
    return { label, level: 'off', percent: 0, toneFg: TONE.off.fg, toneSolid: TONE.off.solid };
  }

  const elapsedHours = Math.max(0, now - started) / HOUR_MS;
  const level: StageClockLevel =
    elapsedHours >= clock.criticalHours ? 'critical'
    : elapsedHours >= clock.warningHours ? 'warning'
    : 'ok';

  // `elapsedHours` já está travado em >= 0 acima (clock skew de `stageEnteredAt`
  // no futuro não pode virar tempo negativo). Com isso, `raw` NUNCA é negativo:
  // `criticalHours > 0` ⟹ elapsedHours/criticalHours >= 0; `criticalHours <= 0`
  // cai no `: 0` do ternário. Um segundo `Math.max(0, …)` aqui seria redundante
  // com o de cima — e essa redundância é exatamente o que deixava o clamp de
  // `elapsedHours` como mutante equivalente (achado da mutação, Rodada 1 da
  // C11): removê-lo não mudava `percent` nem `level` porque este segundo piso
  // escondia a diferença. Um piso só, aqui em cima — o de baixo agora é só teto.
  const raw = clock.criticalHours > 0 ? (elapsedHours / clock.criticalHours) * 100 : 0;
  const percent = Math.min(100, Math.round(raw));

  return { label, level, percent, toneFg: TONE[level].fg, toneSolid: TONE[level].solid };
}
